import type { FC } from "react";
import {
  IconNameEnum,
  useCfarStyles,
  useModuleBEM,
} from "@b2bportal/core-themes";
import { CoreFlightsCfarOffersComponents } from "@b2bportal/core-types";
import { Trans, useI18nContext } from "@hopper-b2b/i18n";
import {
  Button,
  ButtonTypeEnum,
  Dialog,
  DialogClose,
  DialogContent,
  DialogTrigger,
  Icon,
} from "@tenant/ui";
import DOMPurify from "dompurify";

import defaultStyles from "./HtsAirCfarHowItWorksDialog.module.scss";
import { useHtsAirCfarHowItWorksDialog } from "./useHtsAirCfarHowItWorksDialog";

const COMPONENT_KEY =
  CoreFlightsCfarOffersComponents.HtsAirCfarHowItWorksDialog;

export interface HtsAirCfarHowItWorksDialogProps {
  merchandisingContents: Record<string, string>;
  onUpgradeAndContinue?: () => void;
  termsAndConditionsLink?: string;
}

export const HtsAirCfarHowItWorksDialog: FC<
  HtsAirCfarHowItWorksDialogProps
> = ({
  merchandisingContents,
  onUpgradeAndContinue,
  termsAndConditionsLink,
}) => {
  const [, cn] = useModuleBEM(
    useCfarStyles(COMPONENT_KEY, defaultStyles),
    COMPONENT_KEY
  );

  const { t, brand } = useI18nContext();

  const tcLink =
    termsAndConditionsLink ??
    brand.urls.airCfarTerms ??
    brand.urls.cfarTerms ??
    brand.urls.termsAndConditions ??
    "";

  const {
    title,
    subtitle,
    disclaimer,
    howItWorksBullets,
    importantInfoHeader,
    importantInfoBullets,
    importantInfoRedemptionRate,
    termsAndConditions,
  } = useHtsAirCfarHowItWorksDialog({ merchandisingContents });

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button className={cn("trigger")} type="button">
          <Icon
            useCurrentColor
            iconName={IconNameEnum.cfar__info}
            className={cn("icon")}
          />
        </button>
      </DialogTrigger>
      <DialogContent
        title={title}
        className={cn("dialog")}
        bottomFixedContent={
          onUpgradeAndContinue != null ? (
            <DialogClose asChild>
              <Button
                buttonType={ButtonTypeEnum.Primary}
                className={cn("continue-button")}
                onClick={onUpgradeAndContinue}
              >
                {t("core-flights-cfar.howItWorksDialog.upgradeAndContinue", {
                  defaultValue: "Upgrade and continue",
                })}
              </Button>
            </DialogClose>
          ) : undefined
        }
      >
        <div className={cn("content")}>
          {subtitle && (
            <p
              className={cn("subtitle")}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(subtitle),
              }}
            />
          )}
          {howItWorksBullets.length > 0 && (
            <ul className={cn("bullet-list")}>
              {howItWorksBullets.map((bullet) => (
                <li key={bullet} className={cn("bullet-item")}>
                  <Icon
                    iconName={IconNameEnum.checkFilled}
                    className={cn("bullet-icon")}
                  />
                  <span
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(bullet),
                    }}
                  />
                </li>
              ))}
            </ul>
          )}
          {importantInfoBullets.length > 0 && (
            <>
              {importantInfoHeader && (
                <p
                  className={cn("important-info-header")}
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(importantInfoHeader),
                  }}
                />
              )}
              <ul className={cn("bullet-list")}>
                {importantInfoBullets.map((bullet, index) => (
                  <>
                    <li key={bullet} className={cn("bullet-item")}>
                      <Icon
                        iconName={
                          index === 0 ? IconNameEnum.users : IconNameEnum.info
                        }
                        className={cn("bullet-icon")}
                      />
                      <span
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(bullet),
                        }}
                      />
                    </li>
                    {index === 0 && importantInfoRedemptionRate !== "" && (
                      <li
                        key="important-info-redemption-rate"
                        className={cn("bullet-item")}
                      >
                        <Icon
                          iconName={IconNameEnum.creditCard}
                          className={cn("bullet-icon")}
                        />
                        <span
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(
                              importantInfoRedemptionRate
                            ),
                          }}
                        />
                      </li>
                    )}
                  </>
                ))}
              </ul>
            </>
          )}
          {disclaimer && (
            <div className={cn("disclaimer")}>
              <Icon
                iconName={IconNameEnum.cfar__notAllowed}
                className={cn("disclaimer-icon")}
              />
              <p
                className={cn("disclaimer-text")}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(disclaimer),
                }}
              />
            </div>
          )}
          {termsAndConditions ? (
            <p
              className={cn("terms")}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(termsAndConditions),
              }}
            />
          ) : (
            <p className={cn("terms")}>
              <Trans
                i18nKey="core-flights-cfar.howItWorksDialog.terms"
                defaults="For additional details on the service, please review the full <linkText>Terms & Conditions</linkText>."
                components={{
                  linkText: (
                    // eslint-disable-next-line jsx-a11y/anchor-has-content -- content is injected by Trans component
                    <a
                      href={tcLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={cn("terms-link")}
                    />
                  ),
                }}
              />
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
